import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { copilotApi } from '../api/chatApi';
import { AppConfig } from '../../../Config';
import { fetchAuthSession } from '@aws-amplify/auth';
import { getCurrentOrganization } from 'common/selectors/auth';
import { useSelector } from 'react-redux';
import ChatHeader from './ChatHeader';
import ThreadList from './ThreadList';
import MessagesArea from './MessagesArea';
import ChatInputArea from './ChatInputArea';
import { CopilotFilePreviewModal } from './CopilotFilePreviewModal';
import { FileViewModal } from '../../settings/repository-management/components/FileViewModal';
import { useWebSocketChat } from '../hooks/useWebSocketChat';
import { usePageCopilotPrompts } from './CopilotPromptsContext';
import { AIPromptButtons } from './CopilotPromptButtons';

interface CopilotFloatingChatProps {
  isOpen: boolean;
  onNewMessage: () => void;
  onClose: () => void;
}

const CopilotFloatingChat: React.FC<CopilotFloatingChatProps> = ({
  isOpen,
  onNewMessage,
  onClose,
}) => {
  const [threadKey, setThreadKey] = useState<number | undefined>(undefined);
  const [token, setToken] = useState<string | null>(null);
  const previousRoomKey = useRef(threadKey);
  const currentOrganization = useSelector(getCurrentOrganization);

  const getAccessToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Error fetching ID token:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    getAccessToken().then(accessToken => {
      if (mounted) {
        setToken(accessToken);
      }
    });
    return () => {
      mounted = false;
    };
  }, [getAccessToken]);

  const [promptText, setPromptText] = useState<string>('');
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [deleteThread] = copilotApi.useApiPortalV1ChatCopilotDestroyMutation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showThreadList, setShowThreadList] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(false);

  // Fetch threads for list
  const { data: conversations, isLoading: isLoadingThreads } =
    copilotApi.useApiPortalV1ChatCopilotListQuery();

  const [createThread] = copilotApi.useApiPortalV1ChatCopilotCreateMutation();
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<{
    href: string;
    fileId?: string;
    fileUrl?: string;
    label: string;
  } | null>(null);
  const {
    prompts: pagePrompts,
    infoItems: pageInfoItems,
    infoTitle: pageInfoTitle,
    infoIntro: pageInfoIntro,
  } = usePageCopilotPrompts();

  const fallbackSuggestedPrompts = AIPromptButtons.map(button => ({
    id: button.key,
    title: button.title,
    prompt: button.prompt,
    displayText: button.description,
  }));

  // Use the WebSocket hook
  const {
    ws,
    isConnected,
    connectionError,
    aiConnectionStatus,
    isProcessingPrompt,
    messagesByThread,
    setProcessingPrompt,
    setMessagesByThread,
    handleRoomChange,
    resetConnection,
  } = useWebSocketChat({
    threadKey,
    token,
    organizationId: currentOrganization?.id,
    onNewMessage,
  });

  async function startNewThread(prompt: string) {
    const thread = await createThread({
      chat: {
        name: prompt.substring(0, 50),
      },
    });
    if (thread.error) {
      console.error(thread.error);
    } else {
      setThreadKey(thread.data?.id);
    }
    return thread.data?.id;
  }

  const addLocalUserMessage = (threadId: number, messageText: string) => {
    setMessagesByThread(prev => {
      const currentMessages = prev[threadId.toString()] || [];
      return {
        ...prev,
        [threadId.toString()]: [
          ...currentMessages,
          { id: -Date.now(), role: 1, message: messageText },
        ],
      };
    });
  };

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }

  const handleSendMessage = async () => {
    if (!promptText.trim()) {
      return;
    }

    const messageToSend = promptText.trim();
    setPromptText('');

    // If no thread exists, create one first and store the message
    if (!threadKey) {
      setPendingMessage(messageToSend);
      const newThreadKey = await startNewThread(messageToSend);
      if (!newThreadKey) {
        setPendingMessage(null);
        return; // Failed to create thread
      }
      addLocalUserMessage(newThreadKey, messageToSend);
      setThreadKey(newThreadKey);
      // The useEffect will handle sending once connected
      return;
    }

    // Thread exists, send message if connected
    if (ws.current?.readyState !== WebSocket.OPEN) {
      return;
    }

    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      addLocalUserMessage(threadKey, messageToSend);
      setProcessingPrompt(true);
      ws.current.send(
        JSON.stringify({
          type: 'message',
          message: messageToSend,
        })
      );
    }
  };

  const exportThread = async (threadId: number) => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken || !currentOrganization?.id) {
        return;
      }

      const res = await fetch(
        `${AppConfig.api.apiUrl}/api/portal/v1/chat/copilot/${threadId}/export/`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'x-nulogic-organization-id': currentOrganization.id.toString(),
          },
        }
      );
      if (!res.ok) {
        return;
      }

      const disposition = res.headers.get('Content-Disposition');
      const filenameMatch = disposition?.match(/filename="(.+)"/);
      let filename = filenameMatch?.[1] || 'conversation.txt';
      if (filename.endsWith('.md')) {
        filename = filename.replace(/\.md$/i, '.txt');
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // Download failed silently
    }
  };

  useEffect(() => {
    const idleTimer = setTimeout(
      () => {
        if (ws.current) {
          ws.current.close();
        }
      },
      10 * 60 * 1000
    );
    return () => clearTimeout(idleTimer);
  }, [ws]);

  useEffect(() => {
    if (previousRoomKey.current === threadKey) {
      return;
    }
    previousRoomKey.current = threadKey;
    handleRoomChange();
    scrollToBottom();
  }, [handleRoomChange, threadKey]);

  useEffect(() => {
    // Send pending message once thread is created and connected
    if (threadKey && isConnected && pendingMessage) {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        setProcessingPrompt(true);
        ws.current.send(
          JSON.stringify({
            type: 'message',
            message: pendingMessage,
          })
        );
        setPendingMessage(null);
      }
    }
  }, [threadKey, isConnected, pendingMessage, ws, setProcessingPrompt]);

  useEffect(() => {
    if (isProcessingPrompt) {
      scrollToBottom();
    }
  }, [isProcessingPrompt]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  const handleThreadSelect = (threadId: number) => {
    setThreadKey(threadId);
    setShowThreadList(false);
    setShowInfoPanel(false);
  };

  const handlePromptSuggestionClick = async (prompt: string) => {
    setShowThreadList(false);
    setShowInfoPanel(false);
    resetConnection();
    setThreadKey(undefined);
    setPendingMessage(prompt);
    const newThreadKey = await startNewThread(prompt);
    if (!newThreadKey) {
      setPendingMessage(null);
      return;
    }
    addLocalUserMessage(newThreadKey, prompt);
    setThreadKey(newThreadKey);
  };

  const handleThreadDelete = async (e: React.MouseEvent, threadId: number) => {
    e.stopPropagation(); // Prevent thread selection when clicking delete
    if (window.confirm('Are you sure you want to delete this thread?')) {
      await deleteThread({ id: threadId });
      // If deleted thread was the current one, clear it
      if (threadKey === threadId) {
        setThreadKey(undefined);
        setShowInfoPanel(false);
        // Close WebSocket connection
        if (ws.current) {
          ws.current.close();
        }
      }
      // Remove messages for deleted thread
      setMessagesByThread(prev => {
        const updated = { ...prev };
        delete updated[threadId.toString()];
        return updated;
      });
    }
  };

  const isSubmitDisabled = promptText.trim().length === 0;

  if (!isOpen) {
    return null;
  }

  const hasConversations = !!(
    conversations &&
    Array.isArray(conversations) &&
    conversations.length > 0
  );

  return (
    <ChatWrapper>
      <ChatHeader
        showThreadList={showThreadList}
        showInfoPanel={showInfoPanel}
        onBackClick={() => {
          setShowThreadList(false);
          setShowInfoPanel(false);
        }}
        onThreadListClick={() => {
          setShowThreadList(true);
          setShowInfoPanel(false);
        }}
        onInfoClick={() => {
          setShowInfoPanel(prev => !prev);
          setShowThreadList(false);
        }}
        onNewConversation={() => {
          setThreadKey(undefined);
          resetConnection();
          setPendingMessage(null);
          setShowThreadList(false);
          setShowInfoPanel(false);
          setMessagesByThread({});
          setPromptText('');
        }}
        onExport={threadKey && !showThreadList && !showInfoPanel ? () => exportThread(threadKey) : undefined}
        onClose={onClose}
        hasConversations={hasConversations}
        aiConnectionStatus={aiConnectionStatus}
        showConnectionStatus={!!threadKey}
      />

      {showThreadList ? (
        <ThreadList
          conversations={conversations || []}
          isLoading={isLoadingThreads}
          currentThreadId={threadKey}
          onThreadSelect={handleThreadSelect}
          onThreadDelete={handleThreadDelete}
        />
      ) : showInfoPanel ? (
        <InfoPanel>
          {pageInfoTitle && <InfoPanelHeader>{pageInfoTitle}</InfoPanelHeader>}
          {pageInfoIntro && <InfoIntro>{pageInfoIntro}</InfoIntro>}
          {pageInfoItems && pageInfoItems.length > 0 ? (
            <InfoItemList>
              {pageInfoItems.map(item => (
                <InfoItem key={item.term}>
                  <InfoTerm>{item.term}</InfoTerm>
                  <InfoDescription>{item.description}</InfoDescription>
                </InfoItem>
              ))}
            </InfoItemList>
          ) : (
            <InfoEmpty>No additional page information is available.</InfoEmpty>
          )}
        </InfoPanel>
      ) : (
        <>
          <MessagesArea
            threadKey={threadKey}
            connectionError={connectionError}
            isConnected={isConnected}
            messages={threadKey ? messagesByThread[threadKey] || [] : []}
            isProcessingPrompt={isProcessingPrompt}
            aiConnectionStatus={aiConnectionStatus}
            messagesEndRef={messagesEndRef}
            scrollContainerRef={scrollContainerRef}
            onRetry={() => handleRoomChange()}
            suggestedPrompts={
              pagePrompts && pagePrompts.length > 0 ? pagePrompts : fallbackSuggestedPrompts
            }
            onPromptSelect={handlePromptSuggestionClick}
            onFileLinkClick={setPreviewFile}
          />

          <ChatInputArea
            value={promptText}
            onChange={e => setPromptText(e.target.value)}
            onKeyDown={handleKeyDown}
            onSend={handleSendMessage}
            disabled={isSubmitDisabled}
          />

          {previewFile?.fileId ? (
            <FileViewModal
              fileId={previewFile.fileId}
              isOpen={Boolean(previewFile)}
              title={previewFile.label}
              setModalOpen={(open: boolean) => {
                if (!open) {
                  setPreviewFile(null);
                }
              }}
            />
          ) : (
            <CopilotFilePreviewModal
              file={previewFile}
              isOpen={Boolean(previewFile)}
              onClose={() => setPreviewFile(null)}
            />
          )}
        </>
      )}
    </ChatWrapper>
  );
};

export default CopilotFloatingChat;

const ChatWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const InfoPanel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  padding: 24px 28px;
  background: white;
  font-family: 'Poppins', sans-serif;
`;

const InfoPanelHeader = styled.div`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #333333;
  font-family: 'Poppins', sans-serif;
`;

const InfoIntro = styled.div`
  font-size: 13px;
  color: #777777;
  line-height: 1.65;
  margin-bottom: 24px;
  font-family: 'Poppins', sans-serif;
`;

const InfoItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InfoItem = styled.div`
  padding: 0;
  background: none;
  border: none;
`;

const InfoTerm = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #333333;
  margin-bottom: 8px;
  font-family: 'Poppins', sans-serif;
`;

const InfoDescription = styled.div`
  font-size: 13px;
  color: #777777;
  line-height: 1.65;
  font-family: 'Poppins', sans-serif;
`;

const InfoEmpty = styled.div`
  color: #6b7280;
  font-size: 13px;
`;
